import React, { useCallback, useState } from 'react';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import { useNavigate, useParams } from 'react-router-dom';

import * as api from 'api/methods';

import { DEFAULT_LESSON_LOCALE } from 'constants/lessonLocale';

import { currentCourseSelector } from 'store/course/selectors';
import { updateLessonById } from 'store/lesson/actions';
import { currentLessonSelector } from 'store/lesson/selectors';

import { showToast } from 'helpers/showToast';
import { getLessonPreviewPathByCourseType } from 'helpers/getLessonPathByCourseType';
import { getTranslation } from 'helpers/getTranslation';
import { isLessonReadOnly } from 'helpers/isLessonReadOnly';
import { mergeFigmaLessonPreviewsFromBase } from 'helpers/lessonAssetPaths.helpers';
import { getChangedStructuralProperties } from 'helpers/lessonLocalization.helpers';

import { useStatusChange } from 'hooks/useStatusChange';
import { useModal } from 'hooks/useModal';
import { useLocalizedLessonSaveHandlers } from 'hooks/useLocalizedLessonSaveHandlers';

import { convertDataToApi } from './helpers/convertDataToApi';
import { getChangedProperties } from './helpers/getChangedProperties';
import { getResyncData } from './helpers/getResyncData';

import { getDefaultStep } from './FigmaLessonPage.helpers';

import { useFigmaFileImages, useFigmaFileMetadata } from './FigmaLessonPage.hooks';

import FigmaLessonPage from './FigmaLessonPage';

const FigmaLessonPageContainer = (props: any) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const { courseId, lessonId } = useParams();

  const course = useAppSelector(currentCourseSelector);
  const lesson = useAppSelector(currentLessonSelector);

  const enhanceFigmaSaveData = useCallback(
    (data: Record<string, any>) => mergeFigmaLessonPreviewsFromBase(lesson, data),
    [lesson],
  );

  const [saveLoading, setSaveLoading] = useState(false);
  const [autoSaveLoading, setAutoSaveLoading] = useState(false);
  const [currentStep, setCurrentStep] = useState(getDefaultStep(lesson));
  const [currentTask, setCurrentTask] = useState('INITIAL');
  const [resyncLoading, setResyncLoading] = useState(false);
  const [continueLoading, setContinueLoading] = useState(false);
  const [translateAllTasksLoading, setTranslateAllTasksLoading] =
    useState(false);
  const [lastActionAt, setLastActionAt] = useState(lesson.updatedAt);

  const [onStatusContextItemClick] = useStatusChange(lesson);
  const [figmaFileImagesLoading, figmaFileImages] = useFigmaFileImages(
    lesson.fileId,
  );
  const {
    loading: figmaFileMetadataLoading,
    fileName: figmaFileName,
    filePageName: figmaFilePageName,
    haveToken: hasFigmaToken,
  } = useFigmaFileMetadata({
    fileId: lesson.fileId,
    filePageId: lesson.filePageId,
  });

  const persistFigmaPreviews = async ({
    tasks,
    initialState,
    ...restValues
  }: {
    tasks: any[];
    initialState: any;
    [key: string]: any;
  }) => {
    const apiPayload = convertDataToApi({
      ...restValues,
      tasks,
      initialState,
      title: restValues.title || '',
    });

    const previewPayload = await api.saveFigmaLessonPreviews({
      lessonId: lesson.id,
      data: {
        tasks: apiPayload.tasks,
        initialState: apiPayload.initialState,
      },
    });

    const mergedPreviews = mergeFigmaLessonPreviewsFromBase(lesson, {
      tasks:
        previewPayload?.tasks ??
        tasks.map((task) => ({
          preview: task.preview,
        })),
      initialState:
        previewPayload?.initialState ??
        (initialState
          ? {
              preview: initialState.preview,
            }
          : undefined),
      resultPreview: previewPayload?.resultPreview,
    });

    return {
      tasks: tasks.map((task, taskIndex) => ({
        ...task,
        preview: mergedPreviews.tasks?.[taskIndex]?.preview ?? task.preview,
      })),
      initialState: initialState
        ? {
            ...initialState,
            preview:
              mergedPreviews.initialState?.preview ?? initialState.preview,
          }
        : null,
      resultPreview: mergedPreviews.resultPreview,
    };
  };

  const [lessonsOpen, openLessons, closeLessons] = useModal({
    defaultOpen: false,
  });

  const {
    activeLocale,
    availableLocales,
    baseLesson,
    editorLesson,
    localeContentLoading,
    localesWithContent,
    mergedLessonLoading,
    onLocaleChange,
    generateFromLocale,
    handleAutoSaveSubmit,
    handleSaveClick,
    refreshLocalizations,
    syncLocales,
    updateLocaleTabCompletionFromFormTasks,
  } = useLocalizedLessonSaveHandlers({
    lessonId: lessonId as string,
    lessonType: lesson.type,
    baseLesson: lesson,
    convertDataToApi,
    getChangedProperties,
    canAutoSave: (values) => lessonId === values.id && currentStep !== 2,
    enhanceSaveData: enhanceFigmaSaveData,
  });

  const handleTaskTranslated = async () => {
    await refreshLocalizations();
    await syncLocales({ applyActiveLocale: activeLocale });
  };

  const handleTranslateAllTasksFromRu = async () => {
    if (activeLocale !== 'en') {
      return;
    }

    setTranslateAllTasksLoading(true);

    try {
      await generateFromLocale({
        sourceLocale: DEFAULT_LESSON_LOCALE,
        fields: ['tasks'],
      });

      showToast({
        data: {
          variant: 'success',
          title: getTranslation('common.toast.lesson_saved.title'),
          text: getTranslation(
            'figma_lesson_page.translate_all_tasks_success',
          ),
        },
      });
    } catch (error) {
      console.warn(error);

      showToast({
        data: {
          variant: 'error',
          title: getTranslation('common.toast.error.title'),
          text: getTranslation('figma_lesson_page.translate_all_tasks_error'),
        },
      });
    } finally {
      setTranslateAllTasksLoading(false);
    }
  };

  const onAutoSaveSubmit = async (values: any) => {
    setAutoSaveLoading(true);

    const updatedAt = await handleAutoSaveSubmit(values);

    if (updatedAt) {
      setLastActionAt(updatedAt);
    }

    setAutoSaveLoading(false);
  };

  const onSaveClick = async (values: any) => {
    setSaveLoading(true);

    try {
      const updatedAt = await handleSaveClick(values);
      setLastActionAt(updatedAt);

      showToast({
        data: {
          variant: 'success',
          title: getTranslation('common.toast.lesson_saved.title'),
          text: getTranslation('common.toast.lesson_saved.text'),
        },
      });
    } catch (error) {
      showToast({
        data: {
          variant: 'error',
          title: getTranslation('common.toast.error.title'),
          text: getTranslation('common.toast.error.text'),
        },
      });
    }

    setSaveLoading(false);
  };

  const handleResyncClick = async (
    values: any,
    { setFieldValue }: { setFieldValue: any },
  ) => {
    const { fileId, filePageId, designType, tasks, initialState } = values;

    setResyncLoading(true);

    const resyncData = await getResyncData({
      fileId,
      filePageId,
      currentTasks: tasks,
      currentInitialState: initialState,
      designType,
    });

    if (!resyncData) {
      setResyncLoading(false);

      return;
    }

    const [
      updatedTasks,
      updatedInitialState,
      updatedDesignType,
      lessonMaterials,
    ] = resyncData;

    await api.saveFigmaLessonMaterials({
      lessonId: lesson.id,
      data: {
        materials: lessonMaterials || [],
      },
    });

    const previewPayload = await persistFigmaPreviews({
      tasks: updatedTasks,
      initialState: updatedInitialState,
    });

    setFieldValue('initialState', previewPayload.initialState);
    setFieldValue('tasks', previewPayload.tasks);
    setFieldValue('designType', updatedDesignType);

    const resyncSaveData = enhanceFigmaSaveData(
      convertDataToApi({
        title: values.title || '',
        fileId: values.fileId,
        filePageId: values.filePageId,
        designType: updatedDesignType,
        free: values.free,
        initialState: previewPayload.initialState,
        tasks: previewPayload.tasks,
        goals: values.goals,
        description: values.description,
        goodToKnowBlocks: values.goodToKnowBlocks,
        canvasTheory: values.canvasTheory,
        percentToComplete: values.percentToComplete,
        resultRefPreview: values.resultRefPreview,
        categories: values.categories,
      }),
    );

    const resyncChangedPropertyKeys = [
      ...new Set([
        ...getChangedStructuralProperties({
          lessonType: lesson.type,
          changedProperties: getChangedProperties({
            currentLesson: lesson,
            nextLesson: resyncSaveData,
          }),
        }),
        ...(previewPayload.resultPreview &&
        previewPayload.resultPreview !== lesson.resultPreview
          ? (['resultPreview'] as const)
          : []),
      ]),
    ];

    if (resyncChangedPropertyKeys.length) {
      const updatedLesson = await dispatch(
        updateLessonById({
          lessonId: lesson.id,
          lessonType: lesson.type,
          data: resyncSaveData,
          changedPropertiesKeys: resyncChangedPropertyKeys,
        }),
      ).unwrap();

      if (updatedLesson?.updatedAt) {
        setLastActionAt(updatedLesson.updatedAt);
      }
    }

    setResyncLoading(false);

    showToast({
      data: {
        variant: 'success',
        title: getTranslation('common.toast.resync_success.title'),
        text: getTranslation('common.toast.resync_success.text'),
      },
    });
  };

  const handlePreviewClick = () => {
    navigate(
      getLessonPreviewPathByCourseType({
        courseType: course.type,
        courseId,
        lessonId,
      }),
    );
  };

  const handleContinueCreationClick = async (
    values: any,
    { setFieldValue }: { setFieldValue: any },
  ) => {
    const { fileId, filePageId, designType, tasks, initialState } = values;

    setContinueLoading(true);

    const [
      updatedTasks,
      updatedInitialState,
      updatedDesignType,
      lessonMaterials,
    ] = await getResyncData({
      fileId,
      filePageId,
      currentTasks: tasks,
      currentInitialState: initialState,
      designType,
    });

    await api.saveFigmaLessonMaterials({
      lessonId: lesson.id,
      data: {
        materials: lessonMaterials || [],
      },
    });

    const previewPayload = await persistFigmaPreviews({
      tasks: updatedTasks,
      initialState: updatedInitialState,
    });

    setFieldValue('initialState', previewPayload.initialState);
    setFieldValue('tasks', previewPayload.tasks);
    setFieldValue('designType', updatedDesignType);

    const data = enhanceFigmaSaveData(
      convertDataToApi({
        title: '',
        fileId: values.fileId,
        filePageId: values.filePageId,
        designType: updatedDesignType,
        free: values.free,
        initialState: previewPayload.initialState,
        tasks: previewPayload.tasks,
      }),
    );

    const changedPropertiesKeys = getChangedStructuralProperties({
      lessonType: lesson.type,
      changedProperties: getChangedProperties({
        currentLesson: lesson,
        nextLesson: data,
      }),
    });

    if (changedPropertiesKeys.length) {
      const updatedLesson = await dispatch(
        updateLessonById({
          lessonId,
          lessonType: lesson.type,
          data,
          changedPropertiesKeys,
        }),
      ).unwrap();

      setLastActionAt(updatedLesson.updatedAt);
    }

    setContinueLoading(false);

    setCurrentStep(3);
  };

  const handleTaskClick = (nextTask: any) => {
    setCurrentTask(nextTask);
  };

  const handleInitialStateClick = () => {
    setCurrentTask('INITIAL');
  };

  const handleTaskByReferenceClick = () => {
    setCurrentTask('BY_REFERENCE');
  };

  const handleTaskByDescriptionClick = () => {
    setCurrentTask('BY_TASK_DESCRIPTION');
  };

  const handleGoodToKnowClick = () => {
    setCurrentTask('GOOD_TO_KNOW');
  };

  const handleCanvasTheoryClick = () => {
    setCurrentTask('CANVAS_THEORY');
  };

  const lessonReadOnly = isLessonReadOnly(lesson.status);
  const canTranslateAllTasksFromRu =
    activeLocale === 'en' &&
    localesWithContent.includes(DEFAULT_LESSON_LOCALE) &&
    !lessonReadOnly;
  const loading = figmaFileImagesLoading;

  return (
    <>
      <FigmaLessonPage
        {...props}
        loading={loading}
        saveLoading={saveLoading}
        autoSaveLoading={autoSaveLoading || mergedLessonLoading}
        resyncLoading={resyncLoading}
        continueLoading={continueLoading}
        lessonsOpen={lessonsOpen}
        readOnly={lessonReadOnly}
        figmaFileImages={figmaFileImages}
        figmaFileMetadataLoading={figmaFileMetadataLoading}
        figmaFileName={figmaFileName}
        figmaFilePageName={figmaFilePageName}
        hasFigmaToken={hasFigmaToken}
        baseLesson={baseLesson}
        lesson={editorLesson}
        lastActionAt={lastActionAt}
        currentStep={currentStep}
        currentTask={currentTask}
        courseTitle={course?.title || getTranslation('common.text.untitled')}
        courseStatus={course?.status}
        isCatalogLesson={!courseId}
        activeLocale={activeLocale}
        availableLocales={availableLocales}
        localesWithContent={localesWithContent}
        localeContentLoading={localeContentLoading}
        lessonType={lesson.type}
        onLocaleChange={onLocaleChange}
        onTaskTranslated={handleTaskTranslated}
        translateAllTasksLoading={translateAllTasksLoading}
        canTranslateAllTasksFromRu={canTranslateAllTasksFromRu}
        onTranslateAllTasksFromRu={handleTranslateAllTasksFromRu}
        onFormTasksChange={updateLocaleTabCompletionFromFormTasks}
        setCurrentStep={setCurrentStep}
        onAutoSaveSubmit={onAutoSaveSubmit}
        onTaskClick={handleTaskClick}
        onInitialStateClick={handleInitialStateClick}
        onTaskByReferenceClick={handleTaskByReferenceClick}
        onTaskByDescriptionClick={handleTaskByDescriptionClick}
        onGoodToKnowClick={handleGoodToKnowClick}
        onCanvasTheoryClick={handleCanvasTheoryClick}
        onSaveClick={onSaveClick}
        onResyncClick={handleResyncClick}
        onPreviewClick={handlePreviewClick}
        onContinueCreationClick={handleContinueCreationClick}
        onLessonsBtnClick={openLessons}
        onLessonsCloseClick={closeLessons}
        onStatusContextItemClick={onStatusContextItemClick}
      />
    </>
  );
};

export default React.memo(FigmaLessonPageContainer) as any;
